import React from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, Home, Award } from "lucide-react";
import { DATA_SOURCES, BIASES } from "@/data/cardGameData";
import { getCardDisplayTitle } from "@/views/cardgame/cardDisplay";

const copy = {
  nl: {
    title: "Goed gedaan!",
    intro: "Je hebt het Data Literacy Card Game afgerond en ontdekt hoe datakeuzes AI-output vormen.",
    summary: "Samenvatting",
    concept: "Je concept",
    round: (round) => `Ronde ${round}`,
    comparisons: "Outputvergelijkingen",
    output1: "Output 1",
    output2: "Output 2",
    reflection: "Teamreflectie",
    takeaway: "Belangrijk inzicht",
    takeawayText:
      "De synthetische gebruikers die je bouwt zijn alleen zo sterk als de data en aannames erachter. Elke kaart die je koos nam sommige stemmen mee en liet andere weg. Echt ontwerponderzoek betekent dat je verder kijkt dan de data die je al hebt.",
    playAgain: "Opnieuw spelen",
    home: "Terug naar home",
  },
  en: {
    title: "Well done!",
    intro: "You've completed the Data Literacy Card Game and discovered how data choices shape AI output.",
    summary: "Game summary",
    concept: "Your concept",
    round: (round) => `Round ${round}`,
    comparisons: "Output comparisons",
    output1: "Output 1",
    output2: "Output 2",
    reflection: "Team reflection",
    takeaway: "Key takeaway",
    takeawayText:
      "The synthetic users you built are only as good as the data and assumptions behind them. Every card you picked included some voices and excluded others. Real design research means going beyond the data you have.",
    playAgain: "Play again",
    home: "Back to home",
  },
};

function getCategoryColor(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return "#5803FD";
  if (BIASES.find((c) => c.id === id)) return "#E85D04";
  return "#905cff";
}

function getCategoryOrder(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return 0;
  if (BIASES.find((c) => c.id === id)) return 1;
  return 2;
}

function sortCardsByCategory(cards) {
  return [...cards].sort((a, b) => getCategoryOrder(a.id) - getCategoryOrder(b.id));
}

export function ConclusionStep({
  concept,
  round1,
  round2,
  reflection,
  comparisonReflections = [],
  onRestart,
  onGoHome,
  language = "nl",
}) {
  const t = copy[language] || copy.nl;
  const completedComparisons = comparisonReflections.filter((item) => item.round1?.trim() && item.round2?.trim());

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border-2 border-[#905cff] bg-[rgba(144,92,255,0.06)] p-6 text-center">
        <Award className="h-10 w-10 mx-auto text-[#905cff] mb-3" />
        <h2 className="font-display text-2xl font-semibold text-black">{t.title}</h2>
        <p className="text-sm text-black/60 mt-2">
          {t.intro}
        </p>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-5 space-y-4">
        <div className="text-xs text-black/40 uppercase tracking-wider">{t.summary}</div>

        <div>
          <div className="text-[11px] text-black/40 uppercase tracking-wider mb-1">{t.concept}</div>
          <p className="text-sm text-black font-medium">{concept}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {[
            { label: t.round(1), cards: round1.cards, color: "#5803FD" },
            { label: t.round(2), cards: round2.cards, color: "#E85D04" },
          ].map(({ label, cards, color }) => (
            <div key={label} className="rounded-xl border border-black/8 p-3">
              <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color }}>{label}</div>
              <div className="space-y-1">
                {sortCardsByCategory(cards).map((c) => (
                  <div key={c.id} className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: getCategoryColor(c.id) }} />
                    <span className="text-xs text-black/60">{getCardDisplayTitle(c, language)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {completedComparisons.length > 0 && (
          <div>
            <div className="text-[11px] text-black/40 uppercase tracking-wider mb-2">{t.comparisons}</div>
            <div className="space-y-3">
              {completedComparisons.map((item) => (
                <div key={item.id} className="rounded-xl border border-black/8 p-3">
                  <div className="font-medium text-sm text-black mb-2">{item[language === "en" ? "questionEn" : "questionNl"]}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5803FD] mb-1">{t.output1}</div>
                      <p className="text-xs text-black/65 leading-relaxed">{item.round1}</p>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#E85D04] mb-1">{t.output2}</div>
                      <p className="text-xs text-black/65 leading-relaxed">{item.round2}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {reflection && (
          <div>
            <div className="text-[11px] text-black/40 uppercase tracking-wider mb-1">{t.reflection}</div>
            <p className="text-sm text-black/70 leading-relaxed">{reflection}</p>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="text-xs text-black/40 uppercase tracking-wider mb-2">{t.takeaway}</div>
        <p className="text-sm text-black/70 leading-relaxed">
          {t.takeawayText}
        </p>
      </div>

      <div className="flex gap-3">
        <Button onClick={onRestart} variant="outline" className="rounded-xl flex-1">
          <RotateCcw className="h-4 w-4 mr-1.5" /> {t.playAgain}
        </Button>
        <Button onClick={onGoHome} className="rounded-xl flex-1 bg-[#905cff] text-white hover:bg-[#5802ff]">
          <Home className="h-4 w-4 mr-1.5" /> {t.home}
        </Button>
      </div>
    </div>
  );
}
